/**
 * Dashboard Page - Main Trading Dashboard
 * Features: Filters, News, Trade Execution, Auto-Refresh
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { alpacaAPI, analysisAPI, botAPI, configAPI, wsbAPI } from '../services/api';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import { Can } from '../components/Can';
import { isOnboardingActive } from '../components/Onboarding';
import {
  TrendingUp, TrendingDown, DollarSign, Activity, BarChart3, Brain,
  Settings, RefreshCw, Search, Zap, AlertTriangle, CheckCircle, XCircle,
  Eye, EyeOff, Clock, ChevronRight, X, ExternalLink, Filter,
  ArrowUpCircle, ArrowDownCircle, MinusCircle, ShoppingCart, Newspaper, Bot,
  Play, Square, Flame, Users, MessageCircle, Target, Award, Briefcase, Timer, Layers, Bitcoin, Shield
} from 'lucide-react';

// Auto-refresh interval in seconds
const AUTO_REFRESH_INTERVAL = 180;

// ============ TOOLTIP COMPONENT ============
const Tooltip = ({ children, content }) => {
  const [show, setShow] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-bg-card border border-border-color rounded-lg shadow-xl text-sm text-text-secondary whitespace-nowrap animate-fade-in">
          {content}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-bg-card" />
        </div>
      )}
    </div>
  );
};

// ============ FILTER BAR ============
const FilterBar = ({ activeFilters, setActiveFilters }) => {
  const filters = [
    { id: 'bullish', label: 'Bullish', icon: ArrowUpCircle, color: 'green', description: 'Stocks with strong upward momentum and positive sentiment' },
    { id: 'bearish', label: 'Bearish', icon: ArrowDownCircle, color: 'red', description: 'Stocks showing weakness or declining sentiment' },
    { id: 'neutral', label: 'Neutral', icon: MinusCircle, color: 'blue', description: 'Stocks in consolidation - waiting for direction' },
    { id: 'highVolume', label: 'High Volume', icon: BarChart3, color: 'purple', description: 'Above average volume - indicates strong interest' },
    { id: 'oversold', label: 'Oversold', icon: TrendingDown, color: 'yellow', description: 'RSI below 30 - potentially undervalued' },
    { id: 'overbought', label: 'Overbought', icon: TrendingUp, color: 'orange', description: 'RSI above 70 - potentially overvalued' },
  ];

  const toggleFilter = (filterId) => {
    setActiveFilters(prev => prev.includes(filterId) ? prev.filter(f => f !== filterId) : [...prev, filterId]);
  };

  const colorClasses = {
    green: 'bg-accent-green/15 text-accent-green border-accent-green/30',
    red: 'bg-accent-red/15 text-accent-red border-accent-red/30',
    blue: 'bg-accent-blue/15 text-accent-blue border-accent-blue/30',
    purple: 'bg-accent-purple/15 text-accent-purple border-accent-purple/30',
    yellow: 'bg-yellow-500/15 text-yellow-500 border-yellow-500/30',
    orange: 'bg-orange-500/15 text-orange-500 border-orange-500/30',
  };

  return (
    <div className="card p-4 mb-6">
      <div className="flex items-center gap-3 mb-3">
        <Filter className="w-5 h-5 text-text-muted" />
        <span className="text-sm font-medium text-text-secondary">Filter Opportunities</span>
        {activeFilters.length > 0 && (
          <button onClick={() => setActiveFilters([])} className="text-xs text-accent-blue hover:underline ml-auto">Clear all</button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {filters.map(filter => {
          const Icon = filter.icon;
          const isActive = activeFilters.includes(filter.id);
          return (
            <Tooltip key={filter.id} content={filter.description}>
              <button
                onClick={() => toggleFilter(filter.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${isActive ? colorClasses[filter.color] : 'bg-bg-tertiary text-text-secondary border-border-color hover:border-text-muted'}`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{filter.label}</span>
              </button>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

// ============ SPARKLINE COMPONENT ============
const Sparkline = ({ data, color = 'blue', height = 40 }) => {
  if (!data || data.length < 2) {
    // Generate fake data for demo
    data = Array.from({ length: 20 }, (_, i) => {
      const trend = Math.sin(i * 0.5) * 20;
      return 50 + trend + (Math.random() - 0.5) * 10;
    });
  }

  const width = 120;
  const padding = 2;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  // Generate SVG path
  const points = data.map((value, index) => {
    const x = padding + (index / (data.length - 1)) * (width - padding * 2);
    const y = height - padding - ((value - min) / range) * (height - padding * 2);
    return { x, y };
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${height} L ${points[0].x} ${height} Z`;

  const colors = {
    blue: { stroke: '#3B82F6', fill: 'url(#blueGradient)' },
    green: { stroke: '#10B981', fill: 'url(#greenGradient)' },
    red: { stroke: '#EF4444', fill: 'url(#redGradient)' },
    purple: { stroke: '#8B5CF6', fill: 'url(#purpleGradient)' },
  };

  const { stroke, fill } = colors[color] || colors.blue;

  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id="blueGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="greenGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#10B981" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="redGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#EF4444" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#EF4444" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="purpleGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={fill} />
      <path d={linePath} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="3" fill={stroke} />
    </svg>
  );
};

// ============ STAT CARD WITH SPARKLINE ============
const StatCard = ({ title, value, change, icon: Icon, color = 'blue', sparkData }) => {
  const colorClasses = { 
    blue: 'bg-accent-blue/15 text-accent-blue', 
    green: 'bg-accent-green/15 text-accent-green', 
    red: 'bg-accent-red/15 text-accent-red', 
    purple: 'bg-accent-purple/15 text-accent-purple' 
  };

  // Determine sparkline color based on change
  const sparkColor = change !== undefined ? (change >= 0 ? 'green' : 'red') : color;

  return (
    <div className="card p-5 relative overflow-hidden group hover:border-border-hover transition-all">
      {/* Background glow */}
      <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl opacity-20 group-hover:opacity-30 transition-opacity ${
        color === 'green' ? 'bg-accent-green' : 
        color === 'red' ? 'bg-accent-red' : 
        color === 'purple' ? 'bg-accent-purple' : 'bg-accent-blue'
      }`} />
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-text-muted text-xs uppercase tracking-wider font-medium">{title}</span>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
        
        {/* Value and Sparkline Row */}
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="font-display text-2xl font-bold tracking-tight">{value}</div>
            {change !== undefined && (
              <div className={`flex items-center gap-1.5 text-sm mt-1 ${change >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                {change >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                <span className="font-mono text-xs">{change >= 0 ? '+' : ''}{change.toFixed(2)}%</span>
                <span className="text-text-muted text-xs">today</span>
              </div>
            )}
          </div>
          
          {/* Sparkline */}
          <div className="opacity-80 group-hover:opacity-100 transition-opacity">
            <Sparkline data={sparkData} color={sparkColor} height={36} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ STRATEGY SELECTOR ============
const StrategySelector = ({ strategy, setStrategy }) => {
  const strategies = [
    { id: 'day', label: 'Day Trading', icon: '⚡', desc: 'Intraday', newsRange: '48 hours' },
    { id: 'swing', label: 'Swing Trading', icon: '🌊', desc: '2-10 days', newsRange: '14 days' },
    { id: 'longterm', label: 'Long Term', icon: '🏔️', desc: 'Months+', newsRange: '30 days' },
  ];

  return (
    <div className="flex gap-3 p-1.5 bg-bg-tertiary rounded-xl">
      {strategies.map((s) => (
        <Tooltip key={s.id} content={`News from past ${s.newsRange}`}>
          <button
            onClick={() => setStrategy(s.id)}
            className={`flex-1 py-3 px-4 rounded-lg transition-all ${strategy === s.id ? 'bg-bg-card border border-accent-blue shadow-lg shadow-accent-blue/20' : 'border border-transparent hover:bg-bg-card/50'}`}
          >
            <div className="text-xl mb-1">{s.icon}</div>
            <div className={`text-sm font-medium ${strategy === s.id ? 'text-text-primary' : 'text-text-secondary'}`}>{s.label}</div>
            <div className="text-xs text-text-muted">{s.desc}</div>
          </button>
        </Tooltip>
      ))}
    </div>
  );
};

// ============ STOCK CARD ============
const StockCard = ({ stock, onAnalyze, onTrade }) => {
  const isPositive = stock.change >= 0;

  return (
    <div className="card p-5 cursor-pointer hover:border-accent-blue hover:-translate-y-0.5 transition-all">
      <div onClick={() => onAnalyze(stock)}>
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="font-mono text-lg font-semibold">{stock.symbol}</div>
            <div className="text-text-muted text-xs">{stock.name}</div>
          </div>
          <span className={`badge ${stock.signal === 'BUY' ? 'badge-green' : stock.signal === 'SELL' ? 'badge-red' : 'badge-blue'}`}>{stock.signal}</span>
        </div>
        <div className="flex justify-between items-end mb-4">
          <div>
            <div className="font-mono text-2xl font-semibold">${stock.price?.toFixed(2)}</div>
            <div className={`flex items-center gap-1 text-sm ${isPositive ? 'text-accent-green' : 'text-accent-red'}`}>
              {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              <span className="font-mono">{isPositive ? '+' : ''}{stock.change?.toFixed(2)}%</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-text-muted mb-1">AI Score</div>
            <div className={`text-xl font-bold ${stock.aiScore >= 70 ? 'gradient-text-green' : stock.aiScore >= 40 ? 'gradient-text-blue' : 'gradient-text-red'}`}>{stock.aiScore}%</div>
          </div>
        </div>
        <div className="h-1 bg-bg-tertiary rounded-full overflow-hidden mb-4">
          <div className={`h-full rounded-full transition-all duration-500 ${stock.aiScore >= 70 ? 'gradient-bg-green' : stock.aiScore >= 40 ? 'gradient-bg-blue' : 'gradient-bg-red'}`} style={{ width: `${stock.aiScore}%` }} />
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={(e) => { e.stopPropagation(); onTrade(stock, 'buy'); }} className="flex-1 py-2 px-3 rounded-lg bg-accent-green/15 text-accent-green text-sm font-medium hover:bg-accent-green/25 transition-colors flex items-center justify-center gap-1">
          <ShoppingCart className="w-4 h-4" />Buy
        </button>
        <button onClick={(e) => { e.stopPropagation(); onTrade(stock, 'sell'); }} className="flex-1 py-2 px-3 rounded-lg bg-accent-red/15 text-accent-red text-sm font-medium hover:bg-accent-red/25 transition-colors flex items-center justify-center gap-1">
          <TrendingDown className="w-4 h-4" />Sell
        </button>
      </div>
    </div>
  );
};

// ============ NEWS SECTION ============
const NewsSection = ({ symbol, strategy }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const response = await analysisAPI.getNews(symbol, strategy);
        setNews(response.data.articles || []);
      } catch (err) { console.error('Failed to fetch news:', err); }
      finally { setLoading(false); }
    };
    fetchNews();
  }, [symbol, strategy]);

  const timeframeLabel = { day: 'Past 48 Hours', swing: 'Past 14 Days', longterm: 'Past 30 Days' };
  const sentimentColors = { bullish: 'text-accent-green bg-accent-green/15', bearish: 'text-accent-red bg-accent-red/15', neutral: 'text-text-secondary bg-bg-tertiary' };

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="card p-4"><div className="h-4 w-3/4 loading-shimmer rounded mb-2" /><div className="h-3 w-full loading-shimmer rounded" /></div>)}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs text-text-muted uppercase tracking-wider flex items-center gap-2"><Newspaper className="w-4 h-4" />Related News</h3>
        <span className="text-xs text-text-muted">{timeframeLabel[strategy]}</span>
      </div>
      {news.length === 0 ? (
        <div className="card p-6 text-center"><Newspaper className="w-8 h-8 text-text-muted mx-auto mb-2" /><p className="text-text-secondary text-sm">No recent news found</p></div>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {news.map((article, i) => (
            <a key={article.id || i} href={article.url} target="_blank" rel="noopener noreferrer" className="card p-4 block hover:border-accent-blue transition-all group">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h4 className="font-medium text-sm leading-tight group-hover:text-accent-blue transition-colors">{article.headline}</h4>
                <ExternalLink className="w-4 h-4 text-text-muted flex-shrink-0 group-hover:text-accent-blue" />
              </div>
              <p className="text-text-secondary text-xs mb-3 line-clamp-2">{article.summary}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${sentimentColors[article.sentiment]}`}>{article.sentiment}</span>
                  <span className="text-xs text-text-muted">{article.source}</span>
                </div>
                <span className="text-xs text-text-muted">{new Date(article.publishedAt).toLocaleDateString()}</span>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ TRADE MODAL ============
const TradeModal = ({ stock, side, onClose, onSuccess }) => {
  const [orderType, setOrderType] = useState('market');
  const [quantity, setQuantity] = useState('1');
  const [limitPrice, setLimitPrice] = useState(stock?.price?.toFixed(2) || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!quantity || parseFloat(quantity) <= 0) { setError('Please enter a valid quantity'); return; }
    if (orderType === 'limit' && (!limitPrice || parseFloat(limitPrice) <= 0)) { setError('Please enter a valid limit price'); return; }

    setLoading(true);
    setError('');

    try {
      const order = { symbol: stock.symbol, qty: parseFloat(quantity), side, type: orderType, timeInForce: 'day' };
      if (orderType === 'limit') order.limitPrice = parseFloat(limitPrice);

      await alpacaAPI.placeOrder(order);
      toast.success(`${side.toUpperCase()} order placed for ${quantity} shares of ${stock.symbol}`);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to place order');
    } finally { setLoading(false); }
  };

  const estimatedTotal = parseFloat(quantity || 0) * parseFloat(orderType === 'limit' ? limitPrice : stock?.price || 0);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content p-6 max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${side === 'buy' ? 'bg-accent-green/15' : 'bg-accent-red/15'}`}>
              {side === 'buy' ? <ShoppingCart className="w-6 h-6 text-accent-green" /> : <TrendingDown className="w-6 h-6 text-accent-red" />}
            </div>
            <div>
              <h2 className="font-display text-xl font-semibold">{side === 'buy' ? 'Buy' : 'Sell'} {stock?.symbol}</h2>
              <p className="text-text-secondary text-sm">{stock?.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="btn-secondary p-2 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="card p-4 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-text-secondary">Current Price</span>
            <span className="font-mono text-xl font-bold">${stock?.price?.toFixed(2)}</span>
          </div>
        </div>

        {error && <div className="mb-4 p-3 rounded-lg bg-accent-red/10 border border-accent-red/20"><p className="text-accent-red text-sm">{error}</p></div>}

        <div className="mb-5">
          <label className="block text-text-secondary text-sm mb-2">Order Type</label>
          <div className="flex gap-2">
            {['market', 'limit'].map(type => (
              <button key={type} onClick={() => setOrderType(type)} className={`flex-1 py-3 px-4 rounded-lg border transition-all ${orderType === type ? 'bg-accent-blue/15 border-accent-blue text-accent-blue' : 'border-border-color text-text-secondary hover:border-text-muted'}`}>
                <div className="font-medium capitalize">{type}</div>
                <div className="text-xs opacity-70">{type === 'market' ? 'Execute immediately' : 'Set your price'}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-5">
          <label className="block text-text-secondary text-sm mb-2">Quantity (Shares)</label>
          <input type="number" min="1" step="1" value={quantity} onChange={e => setQuantity(e.target.value)} className="input font-mono text-lg" placeholder="1" />
        </div>

        {orderType === 'limit' && (
          <div className="mb-5">
            <label className="block text-text-secondary text-sm mb-2">Limit Price ($)</label>
            <input type="number" min="0.01" step="0.01" value={limitPrice} onChange={e => setLimitPrice(e.target.value)} className="input font-mono text-lg" />
          </div>
        )}

        <div className="card p-4 mb-6 bg-bg-tertiary">
          <div className="flex justify-between items-center">
            <span className="text-text-secondary">Estimated Total</span>
            <span className="font-mono text-2xl font-bold">${estimatedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>

        <button onClick={handleSubmit} disabled={loading} className={`w-full py-4 rounded-xl font-semibold text-lg transition-all disabled:opacity-50 ${side === 'buy' ? 'bg-accent-green text-bg-primary hover:shadow-lg hover:shadow-accent-green/30' : 'bg-accent-red text-white hover:shadow-lg hover:shadow-accent-red/30'}`}>
          {loading ? <span className="flex items-center justify-center gap-2"><div className="w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin" />Processing...</span> : `${side === 'buy' ? 'Buy' : 'Sell'} ${quantity || 0} Shares`}
        </button>

        <p className="mt-4 text-center text-text-muted text-xs">⚠️ Paper trading account - no real money involved</p>
      </div>
    </div>
  );
};

// ============ POSITIONS TABLE ============
const PositionsTable = ({ positions, onTrade }) => {
  if (!positions || positions.length === 0) {
    return <div className="card p-8 text-center"><BarChart3 className="w-12 h-12 text-text-muted mx-auto mb-4" /><h3 className="text-lg font-medium mb-2">No Positions Yet</h3><p className="text-text-secondary">Your portfolio positions will appear here.</p></div>;
  }

  return (
    <div className="card overflow-hidden">
      <div className="p-5 border-b border-border-color flex justify-between items-center">
        <h3 className="font-display text-lg font-semibold">Your Positions</h3>
        <span className="text-text-muted text-sm">{positions.length} holdings</span>
      </div>
      <div className="overflow-x-auto">
        <table className="table">
          <thead><tr><th>Symbol</th><th className="text-right">Qty</th><th className="text-right">Avg Cost</th><th className="text-right">Current</th><th className="text-right">P&L</th><th className="text-right">% Change</th><th className="text-center">Actions</th></tr></thead>
          <tbody>
            {positions.map((pos) => (
              <tr key={pos.symbol}>
                <td className="font-mono font-semibold">{pos.symbol}</td>
                <td className="text-right font-mono">{pos.qty}</td>
                <td className="text-right font-mono">${pos.avgCost?.toFixed(2)}</td>
                <td className="text-right font-mono">${pos.currentPrice?.toFixed(2)}</td>
                <td className={`text-right font-mono ${pos.pnl >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>{pos.pnl >= 0 ? '+' : ''}${pos.pnl?.toFixed(2)}</td>
                <td className="text-right"><span className={`badge ${pos.pnlPercent >= 0 ? 'badge-green' : 'badge-red'}`}>{pos.pnlPercent >= 0 ? '+' : ''}{pos.pnlPercent?.toFixed(2)}%</span></td>
                <td className="text-center">
                  <div className="flex justify-center gap-2">
                    <button onClick={() => onTrade({ symbol: pos.symbol, price: pos.currentPrice, name: pos.symbol }, 'buy')} className="px-3 py-1 rounded bg-accent-green/15 text-accent-green text-xs font-medium hover:bg-accent-green/25">Buy</button>
                    <button onClick={() => onTrade({ symbol: pos.symbol, price: pos.currentPrice, name: pos.symbol }, 'sell')} className="px-3 py-1 rounded bg-accent-red/15 text-accent-red text-xs font-medium hover:bg-accent-red/25">Sell</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============ ALPACA SETUP MODAL ============
const AlpacaSetupModal = ({ onClose, onSave }) => {
  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!apiKey || !secretKey) { setError('Both API Key and Secret Key are required'); return; }
    setLoading(true);
    setError('');
    try {
      await alpacaAPI.verifyCredentials(apiKey, secretKey, true);
      await onSave(apiKey, secretKey);
      toast.success('Alpaca connected successfully!');
      onClose();
    } catch (err) { setError(err.response?.data?.error || 'Invalid credentials.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-accent-blue/15 flex items-center justify-center"><Zap className="w-6 h-6 text-accent-blue" /></div>
          <div><h2 className="font-display text-xl font-semibold">Connect Alpaca</h2><p className="text-text-secondary text-sm">Enter your paper trading API keys</p></div>
        </div>
        {error && <div className="mb-6 p-4 rounded-lg bg-accent-red/10 border border-accent-red/20"><p className="text-accent-red text-sm">{error}</p></div>}
        <div className="mb-5"><label className="block text-text-secondary text-sm mb-2">API Key ID</label><input type="text" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="PKHIFX..." className="input font-mono" /></div>
        <div className="mb-6"><label className="block text-text-secondary text-sm mb-2">Secret Key</label><div className="relative"><input type={showSecret ? 'text' : 'password'} value={secretKey} onChange={(e) => setSecretKey(e.target.value)} placeholder="••••••••" className="input font-mono pr-12" /><button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted">{showSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button></div></div>
        <button onClick={handleSave} disabled={loading || !apiKey || !secretKey} className="w-full btn-primary py-4 disabled:opacity-50 flex items-center justify-center gap-2">{loading ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Connecting...</> : 'Connect & Verify'}</button>
        <p className="mt-4 text-center text-text-muted text-xs">🔒 Your credentials are stored locally</p>
      </div>
    </div>
  );
};

// ============ ANALYSIS PANEL ============
const AnalysisPanel = ({ stock, strategy, onClose, onAiSummary, onTrade }) => {
  const [technicalData, setTechnicalData] = useState(null);
  const [fundamentalData, setFundamentalData] = useState(null);
  const [sentimentData, setSentimentData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [tech, fund, sent] = await Promise.all([analysisAPI.getTechnical(stock.symbol), analysisAPI.getFundamental(stock.symbol), analysisAPI.getSentiment(stock.symbol)]);
        setTechnicalData(tech.data);
        setFundamentalData(fund.data);
        setSentimentData(sent.data);
      } catch (err) { console.error('Failed to fetch analysis:', err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [stock.symbol]);

  const technicalIndicators = technicalData ? [
    { name: 'RSI (14)', value: technicalData.rsi?.toFixed(1) || 'N/A', status: technicalData.rsiStatus || 'neutral' },
    { name: 'MACD', value: technicalData.macd?.toFixed(2) || 'N/A', status: technicalData.macdStatus || 'neutral' },
    { name: 'SMA 50', value: technicalData.sma50 ? `$${technicalData.sma50.toFixed(2)}` : 'N/A', status: technicalData.aboveSMA50 ? 'bullish' : 'bearish' },
    { name: 'SMA 200', value: technicalData.sma200 ? `$${technicalData.sma200.toFixed(2)}` : 'N/A', status: technicalData.aboveSMA200 ? 'bullish' : 'bearish' },
  ] : [];

  const fundamentals = fundamentalData ? [
    { name: 'P/E Ratio', value: fundamentalData.pe?.toFixed(1) || 'N/A' },
    { name: 'Market Cap', value: fundamentalData.marketCap || 'N/A' },
    { name: 'Profit Margin', value: fundamentalData.profitMargin ? `${fundamentalData.profitMargin.toFixed(1)}%` : 'N/A' },
    { name: '52W High', value: fundamentalData.fiftyTwoWeekHigh ? `$${fundamentalData.fiftyTwoWeekHigh.toFixed(2)}` : 'N/A' },
  ] : [];

  return (
    <div className="fixed top-0 right-0 w-[500px] h-screen bg-bg-secondary border-l border-border-color p-6 overflow-y-auto z-50 animate-slide-in-right">
      <div className="flex justify-between items-center mb-6">
        <div><div className="font-display text-2xl font-bold">{stock.symbol}</div><div className="text-text-secondary">{stock.name}</div></div>
        <button onClick={onClose} className="btn-secondary p-2.5 rounded-xl"><X className="w-5 h-5" /></button>
      </div>

      <div className="card p-5 mb-5">
        <div className="font-mono text-3xl font-bold">${stock.price?.toFixed(2)}</div>
        <div className={`flex items-center gap-2 mt-1 ${stock.change >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
          {stock.change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          <span className="font-mono">{stock.change >= 0 ? '+' : ''}{stock.change?.toFixed(2)}% today</span>
        </div>
      </div>

      <div className="flex gap-3 mb-5">
        <button onClick={() => onTrade(stock, 'buy')} className="flex-1 py-3 rounded-xl bg-accent-green text-bg-primary font-semibold hover:shadow-lg hover:shadow-accent-green/30 transition-all flex items-center justify-center gap-2"><ShoppingCart className="w-5 h-5" />Buy</button>
        <button onClick={() => onTrade(stock, 'sell')} className="flex-1 py-3 rounded-xl bg-accent-red text-white font-semibold hover:shadow-lg hover:shadow-accent-red/30 transition-all flex items-center justify-center gap-2"><TrendingDown className="w-5 h-5" />Sell</button>
      </div>

      <Can perm="action.dashboard.ai-analysis" fallback={
        <div className="w-full py-3 mb-6 text-center text-xs text-text-muted border border-border-color rounded-xl">AI analysis isn't available for your role</div>
      }>
        <button onClick={() => onAiSummary(stock)} className="w-full btn-primary py-4 mb-6 flex items-center justify-center gap-2"><Brain className="w-5 h-5" />Get AI Analysis & Probability</button>
      </Can>

      {loading ? (
        <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="card p-5"><div className="h-4 w-32 loading-shimmer rounded mb-4" /><div className="h-3 loading-shimmer rounded" /></div>)}</div>
      ) : (
        <>
          <div className="mb-6">
            <h3 className="text-xs text-text-muted uppercase tracking-wider mb-3">Technical Indicators</h3>
            <div className="card overflow-hidden">
              {technicalIndicators.map((ind, i) => (
                <div key={ind.name} className={`flex justify-between p-4 ${i < technicalIndicators.length - 1 ? 'border-b border-border-color' : ''}`}>
                  <span className="text-text-secondary">{ind.name}</span>
                  <div className="text-right">
                    <span className="font-mono font-medium">{ind.value}</span>
                    <span className={`ml-2 text-xs ${ind.status === 'bullish' ? 'text-accent-green' : ind.status === 'bearish' ? 'text-accent-red' : 'text-text-muted'}`}>{ind.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-xs text-text-muted uppercase tracking-wider mb-3">Fundamentals</h3>
            <div className="grid grid-cols-2 gap-3">
              {fundamentals.map(f => <div key={f.name} className="card p-4"><div className="text-xs text-text-muted mb-1">{f.name}</div><div className="font-mono font-semibold">{f.value}</div></div>)}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-xs text-text-muted uppercase tracking-wider mb-3">Market Sentiment</h3>
            <div className="card p-5">
              <div className="flex justify-between mb-4"><span>Sentiment Score</span><span className={sentimentData?.score >= 50 ? 'text-accent-green' : 'text-accent-red'}>{sentimentData?.score >= 60 ? 'Bullish' : sentimentData?.score >= 40 ? 'Neutral' : 'Bearish'}</span></div>
              <div className="h-2 bg-bg-tertiary rounded-full overflow-hidden"><div className="h-full gradient-bg-green" style={{ width: `${sentimentData?.score || 50}%` }} /></div>
            </div>
          </div>

          <NewsSection symbol={stock.symbol} strategy={strategy} />
        </>
      )}
    </div>
  );
};

// ============ AI SUMMARY MODAL ============
const AiSummaryModal = ({ stock, onClose }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try { const response = await analysisAPI.getSummary(stock.symbol); setSummary(response.data); }
      catch (err) { console.error('Failed to fetch summary:', err); }
      finally { setLoading(false); }
    };
    fetchSummary();
  }, [stock.symbol]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="bg-bg-secondary border border-border-color rounded-2xl max-w-2xl w-[95%] max-h-[90vh] overflow-auto animate-slide-up" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-bg-secondary border-b border-border-color p-6 flex justify-between items-center z-10">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl gradient-bg-blue flex items-center justify-center"><Brain className="w-7 h-7 text-white" /></div>
            <div><h2 className="font-display text-xl font-semibold">AI Analysis: {stock.symbol}</h2><p className="text-text-secondary">{stock.name}</p></div>
          </div>
          <button onClick={onClose} className="btn-secondary p-2.5 rounded-xl"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="text-center py-16"><div className="w-16 h-16 border-3 border-border-color border-t-accent-blue rounded-full animate-spin mx-auto mb-6" /><p className="text-text-secondary">Analyzing...</p></div>
          ) : summary ? (
            <>
              <div className="card p-8 text-center mb-6 relative overflow-hidden">
                <div className="text-text-muted text-sm mb-2">PROBABILITY OF SUCCESS</div>
                <div className={`font-display text-7xl font-bold ${summary.overallScore >= 65 ? 'gradient-text-green' : summary.overallScore >= 45 ? 'gradient-text-blue' : 'gradient-text-red'}`}>{summary.overallScore}%</div>
                <div className={`inline-block mt-4 px-6 py-2 rounded-full text-lg font-semibold ${summary.recommendation.includes('BUY') ? 'bg-accent-green/15 text-accent-green' : summary.recommendation === 'HOLD' ? 'bg-accent-blue/15 text-accent-blue' : 'bg-accent-red/15 text-accent-red'}`}>{summary.recommendation}</div>
              </div>
              <div className="space-y-4 mb-6">
                <div className="card p-5"><div className="flex items-center gap-2 mb-3"><Activity className="w-5 h-5 text-accent-blue" /><h4 className="font-semibold">Technical Analysis</h4></div><p className="text-text-secondary leading-relaxed">{summary.analysis?.technical}</p></div>
                <div className="card p-5"><div className="flex items-center gap-2 mb-3"><BarChart3 className="w-5 h-5 text-accent-purple" /><h4 className="font-semibold">Fundamental Analysis</h4></div><p className="text-text-secondary leading-relaxed">{summary.analysis?.fundamental}</p></div>
                <div className="card p-5"><div className="flex items-center gap-2 mb-3"><Brain className="w-5 h-5 text-accent-green" /><h4 className="font-semibold">Sentiment Analysis</h4></div><p className="text-text-secondary leading-relaxed">{summary.analysis?.sentiment}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="card p-5"><h4 className="text-accent-red flex items-center gap-2 mb-3"><AlertTriangle className="w-4 h-4" />Key Risks</h4><ul className="space-y-2 text-text-secondary text-sm">{summary.risks?.map((r,i) => <li key={i}>• {r}</li>)}</ul></div>
                <div className="card p-5"><h4 className="text-accent-green flex items-center gap-2 mb-3"><Zap className="w-4 h-4" />Catalysts</h4><ul className="space-y-2 text-text-secondary text-sm">{summary.catalysts?.map((c,i) => <li key={i}>• {c}</li>)}</ul></div>
              </div>
              <div className="p-4 rounded-xl bg-accent-yellow/10 border border-accent-yellow/20"><p className="text-sm text-accent-yellow">⚠️ <strong>Disclaimer:</strong> This AI analysis is for informational purposes only. Not financial advice.</p></div>
            </>
          ) : <div className="text-center py-16"><XCircle className="w-16 h-16 text-accent-red mx-auto mb-4" /><p className="text-text-secondary">Failed to load analysis.</p></div>}
        </div>
      </div>
    </div>
  );
};

// ============ MARKET INTELLIGENCE ROW (Phase 1-5) ============
const MarketIntelligenceRow = () => {
  const [regimeData, setRegimeData] = useState(null);
  const [propFirmData, setPropFirmData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchIntel = async () => {
      try {
        const [regimeRes, propRes] = await Promise.all([
          analysisAPI.getRegimeSummary().catch(() => ({ data: null })),
          analysisAPI.getPropFirmDashboard().catch(() => ({ data: null })),
        ]);
        if (regimeRes.data) setRegimeData(regimeRes.data);
        if (propRes.data) setPropFirmData(propRes.data);
      } catch (e) { /* silent */ }
      setLoading(false);
    };
    fetchIntel();
    const interval = setInterval(fetchIntel, 120000); // Refresh every 2 min
    return () => clearInterval(interval);
  }, []);

  if (loading) return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
      {[1,2,3].map(i => <div key={i} className="card p-5"><div className="h-20 loading-shimmer rounded" /></div>)}
    </div>
  );

  const regimeColors = {
    STRONG_TREND_UP: 'text-accent-green', STRONG_TREND_DOWN: 'text-accent-red',
    WEAK_TREND: 'text-accent-blue', RANGE_BOUND: 'text-accent-yellow',
    SQUEEZE: 'text-orange-400', CHOPPY: 'text-accent-red', VOLATILE_TREND: 'text-accent-purple',
    UNKNOWN: 'text-text-muted',
  };
  const regimeEmojis = {
    STRONG_TREND_UP: '🚀', STRONG_TREND_DOWN: '🛡️', WEAK_TREND: '⚡',
    RANGE_BOUND: '📊', SQUEEZE: '🔥', CHOPPY: '⚠️', VOLATILE_TREND: '🌊', UNKNOWN: '❓',
  };

  const window = regimeData?.tradingWindow;
  const windowColor = window?.score >= 70 ? 'text-accent-green' : window?.score >= 40 ? 'text-accent-yellow' : 'text-accent-red';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
      {/* Market Regime Card */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-accent-purple" />
          <h3 className="font-semibold text-sm">Market Regime</h3>
        </div>
        {regimeData?.indices ? (
          <div className="space-y-2">
            {Object.entries(regimeData.indices).slice(0, 3).map(([sym, data]) => (
              <div key={sym} className="flex items-center justify-between">
                <span className="font-mono text-sm font-semibold w-10">{sym}</span>
                <span className="text-sm">{regimeEmojis[data.regime] || '❓'}</span>
                <span className={`text-xs font-medium ${regimeColors[data.regime] || 'text-text-muted'}`}>
                  {data.regime?.replace(/_/g, ' ') || 'UNKNOWN'}
                </span>
                <span className="text-xs text-text-muted">ADX {data.adx || '?'}</span>
              </div>
            ))}
            <div className="mt-2 pt-2 border-t border-border-color flex items-center justify-between">
              <span className="text-xs text-text-muted">Overall</span>
              <span className={`text-sm font-semibold ${regimeColors[regimeData.overallRegime]}`}>
                {regimeEmojis[regimeData.overallRegime]} {regimeData.overallProfile}
              </span>
            </div>
          </div>
        ) : <div className="text-text-muted text-sm">Loading regime data...</div>}
      </div>

      {/* Trading Window Card */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-accent-blue" />
          <h3 className="font-semibold text-sm">Trading Window</h3>
        </div>
        {window ? (
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-2xl font-bold ${windowColor}`}>{window.score}</span>
              <span className="text-xs text-text-muted">/100</span>
              <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                window.score >= 70 ? 'bg-accent-green/15 text-accent-green' :
                window.score >= 40 ? 'bg-accent-yellow/15 text-accent-yellow' :
                'bg-accent-red/15 text-accent-red'
              }`}>{window.window?.replace(/_/g, ' ')}</span>
            </div>
            <div className="w-full bg-bg-tertiary rounded-full h-2 mb-2">
              <div className={`h-2 rounded-full transition-all ${
                window.score >= 70 ? 'bg-accent-green' : window.score >= 40 ? 'bg-accent-yellow' : 'bg-accent-red'
              }`} style={{ width: `${window.score}%` }} />
            </div>
            <p className="text-xs text-text-muted">{window.note}</p>
            <div className="mt-2 text-xs text-text-muted">ET: {window.currentTimeET} • {window.isMarketHours ? 'Market Open' : 'Market Closed'}</div>
          </div>
        ) : <div className="text-text-muted text-sm">Loading...</div>}
      </div>

      {/* Prop Firm / Drawdown Card */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4 text-accent-yellow" />
          <h3 className="font-semibold text-sm">Risk Monitor</h3>
        </div>
        {propFirmData?.drawdowns ? (
          <div className="space-y-2">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-text-muted">Daily DD</span>
                <span className={propFirmData.drawdowns.daily > 2 ? 'text-accent-red' : 'text-text-secondary'}>
                  {propFirmData.drawdowns.daily?.toFixed(2) || 0}%
                </span>
              </div>
              <div className="w-full bg-bg-tertiary rounded-full h-1.5">
                <div className={`h-1.5 rounded-full ${propFirmData.drawdowns.daily > 2 ? 'bg-accent-red' : propFirmData.drawdowns.daily > 1 ? 'bg-accent-yellow' : 'bg-accent-green'}`}
                  style={{ width: `${Math.min(100, (propFirmData.drawdowns.daily / 3) * 100)}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-text-muted">Trailing DD</span>
                <span className={propFirmData.drawdowns.trailing > 5 ? 'text-accent-red' : 'text-text-secondary'}>
                  {propFirmData.drawdowns.trailing?.toFixed(2) || 0}%
                </span>
              </div>
              <div className="w-full bg-bg-tertiary rounded-full h-1.5">
                <div className={`h-1.5 rounded-full ${propFirmData.drawdowns.trailing > 5 ? 'bg-accent-red' : propFirmData.drawdowns.trailing > 3 ? 'bg-accent-yellow' : 'bg-accent-green'}`}
                  style={{ width: `${Math.min(100, (propFirmData.drawdowns.trailing / 8) * 100)}%` }} />
              </div>
            </div>
            <div className="flex justify-between pt-1 border-t border-border-color text-xs">
              <span className="text-text-muted">Status</span>
              <span className={`font-semibold ${
                propFirmData.status === 'NORMAL' ? 'text-accent-green' :
                propFirmData.status === 'WARNING' ? 'text-accent-yellow' : 'text-accent-red'
              }`}>{propFirmData.status || 'NORMAL'}</span>
            </div>
            {propFirmData.equity?.highWaterMark && (
              <div className="flex justify-between text-xs">
                <span className="text-text-muted">HWM</span>
                <span className="text-text-secondary">${propFirmData.equity.highWaterMark?.toLocaleString()}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-3">
            <Shield className="w-8 h-8 text-text-muted mx-auto mb-2 opacity-30" />
            <p className="text-xs text-text-muted">Enable Prop Firm Guard in Bot settings for drawdown tracking</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ MAIN DASHBOARD ============
const Dashboard = () => {
  const { user, profile, hasAlpacaKeys, saveAlpacaKeys, logout } = useAuth();
  const [showAlpacaSetup, setShowAlpacaSetup] = useState(false);
  const [strategy, setStrategy] = useState('swing');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [accountData, setAccountData] = useState(null);
  const [positions, setPositions] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [marketClock, setMarketClock] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);
  const [showAiSummary, setShowAiSummary] = useState(null);
  const [tradeModal, setTradeModal] = useState(null);
  const [sparklineData, setSparklineData] = useState({});
  
  // Bot state
  const [botStatus, setBotStatus] = useState({ isRunning: false, stats: null });
  const [dayTradingEnabled, setDayTradingEnabled] = useState(null);
  const [wsbEnabled, setWsbEnabled] = useState(null);
  const [botConfig, setBotConfig] = useState(null);
  
  // WSB state
  const [wsbTrending, setWsbTrending] = useState([]);
  const [wsbLoading, setWsbLoading] = useState(false);
  
  // Alpaca connection status
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  
  // Auto-refresh state
  const [autoRefreshCountdown, setAutoRefreshCountdown] = useState(AUTO_REFRESH_INTERVAL);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  // Generate realistic sparkline data based on current value and change
  const generateSparklineData = useCallback((currentValue, changePercent, points = 20) => {
    if (!currentValue) return null;
    
    const data = [];
    const startValue = currentValue / (1 + (changePercent || 0) / 100);
    const volatility = Math.abs(changePercent || 2) / 100 * 0.3;
    
    for (let i = 0; i < points; i++) {
      const progress = i / (points - 1);
      // Smooth interpolation from start to current with some noise
      const trend = startValue + (currentValue - startValue) * progress;
      const noise = trend * volatility * (Math.random() - 0.5) * 2;
      data.push(trend + noise);
    }
    
    // Ensure last point is close to current value
    data[data.length - 1] = currentValue;
    
    return data;
  }, []);

  // Update sparkline data when account data changes
  useEffect(() => {
    if (accountData) {
      setSparklineData({
        portfolio: generateSparklineData(accountData.portfolioValue, accountData.dayPnlPercent),
        cash: generateSparklineData(accountData.cash, (Math.random() - 0.5) * 2),
        buyingPower: generateSparklineData(accountData.buyingPower, (Math.random() - 0.5) * 3),
        pnl: generateSparklineData(
          Math.abs(accountData.dayPnl || 0) + 100, 
          accountData.dayPnlPercent
        ),
      });
    }
  }, [accountData, generateSparklineData]);

  const fetchData = useCallback(async () => {
    if (!hasAlpacaKeys) { setLoading(false); return; }
    try {
      const [accountRes, positionsRes, opportunitiesRes, clockRes] = await Promise.all([
        alpacaAPI.getAccount().catch(() => null),
        alpacaAPI.getPositions().catch(() => null),
        analysisAPI.getOpportunities(strategy),
        alpacaAPI.getMarketClock().catch(() => null),
      ]);
      if (accountRes?.data) setAccountData(accountRes.data);
      if (positionsRes?.data) setPositions(positionsRes.data);
      if (opportunitiesRes?.data) setOpportunities(opportunitiesRes.data);
      if (clockRes?.data) setMarketClock(clockRes.data);
    } catch (err) { console.error('Failed to fetch data:', err); toast.error('Failed to load data'); }
    finally { setLoading(false); setRefreshing(false); }
  }, [hasAlpacaKeys, strategy]);

  // Fetch bot status
  const fetchBotData = useCallback(async () => {
    try {
      const [statusRes, configRes, dayFlagRes, wsbFlagRes] = await Promise.all([
        botAPI.getStatus().catch(() => null),
        botAPI.getConfig().catch(() => null),
        configAPI.getDayTradingEnabled().catch(() => null),
        configAPI.getWsbEnabled().catch(() => null),
      ]);
      if (statusRes?.data) setBotStatus(statusRes.data);
      if (configRes?.data?.config) setBotConfig(configRes.data.config);
      if (dayFlagRes?.data && typeof dayFlagRes.data.enabled === 'boolean') {
        setDayTradingEnabled(dayFlagRes.data.enabled);
      }
      if (wsbFlagRes?.data && typeof wsbFlagRes.data.enabled === 'boolean') {
        setWsbEnabled(wsbFlagRes.data.enabled);
      }
    } catch (err) { console.error('Failed to fetch bot data:', err); }
  }, []);

  // Fetch WSB data
  const fetchWSBData = useCallback(async () => {
    setWsbLoading(true);
    try {
      const res = await wsbAPI.getTrending(8);
      setWsbTrending(res.data.tickers || []);
    } catch (err) { console.error('Failed to fetch WSB data:', err); }
    finally { setWsbLoading(false); }
  }, []);

  // Fetch Alpaca connection status
  const fetchConnectionStatus = useCallback(async () => {
    if (!hasAlpacaKeys) return;
    try {
      const res = await alpacaAPI.getConnectionStatus();
      setConnectionStatus(res.data);
    } catch (err) { console.error('Failed to fetch connection status:', err); }
  }, [hasAlpacaKeys]);

  useEffect(() => { fetchData(); fetchBotData(); fetchWSBData(); fetchConnectionStatus(); }, [fetchData, fetchBotData, fetchWSBData, fetchConnectionStatus]);
  // Defer this auto-prompt while the first-run onboarding welcome is still active,
  // so the two modals don't stack on a brand-new user.
  useEffect(() => { if (!hasAlpacaKeys && !isOnboardingActive(user?.id)) setShowAlpacaSetup(true); }, [hasAlpacaKeys, user?.id]);
  
  // Refresh bot status every 30 seconds if running
  useEffect(() => {
    if (botStatus.isRunning) {
      const interval = setInterval(fetchBotData, 30000);
      return () => clearInterval(interval);
    }
  }, [botStatus.isRunning, fetchBotData]);

  // Auto-refresh countdown and data refresh every 180 seconds
  useEffect(() => {
    if (!autoRefreshEnabled) return;
    
    const countdownInterval = setInterval(() => {
      setAutoRefreshCountdown(prev => {
        if (prev <= 1) {
          // Trigger refresh
          fetchData();
          fetchBotData();
          fetchWSBData();
          return AUTO_REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [autoRefreshEnabled, fetchData, fetchBotData, fetchWSBData]);

  const handleRefresh = () => { 
    setRefreshing(true); 
    fetchData(); 
    fetchBotData(); 
    fetchWSBData();
    setAutoRefreshCountdown(AUTO_REFRESH_INTERVAL); // Reset countdown on manual refresh
  };
  const handleSaveAlpacaKeys = async (apiKey, secretKey) => { const result = await saveAlpacaKeys(apiKey, secretKey, true); if (result.success) { setShowAlpacaSetup(false); fetchData(); } return result; };
  const handleTrade = (stock, side) => { setTradeModal({ stock, side }); };

  const filteredOpportunities = opportunities.filter(s => {
    const matchesSearch = s.symbol.toLowerCase().includes(searchQuery.toLowerCase()) || s.name?.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (activeFilters.length === 0) return true;
    let matches = false;
    if (activeFilters.includes('bullish') && s.aiScore >= 65) matches = true;
    if (activeFilters.includes('bearish') && s.aiScore < 40) matches = true;
    if (activeFilters.includes('neutral') && s.aiScore >= 40 && s.aiScore < 65) matches = true;
    if (activeFilters.includes('oversold') && s.rsi && s.rsi < 30) matches = true;
    if (activeFilters.includes('overbought') && s.rsi && s.rsi > 70) matches = true;
    if (activeFilters.includes('highVolume')) matches = true;
    return matches;
  });

  return (
    <div className="min-h-screen">
      <Navigation
        onRefresh={handleRefresh}
        refreshing={refreshing}
        marketClock={marketClock}
        connectionStatus={connectionStatus}
        autoRefreshEnabled={autoRefreshEnabled}
        autoRefreshCountdown={autoRefreshCountdown}
        onToggleAutoRefresh={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
        onShowConnectionModal={() => setShowConnectionModal(true)}
      />

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
      {/* Alpaca Connection Modal */}
      {showConnectionModal && connectionStatus && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowConnectionModal(false)}>
          <div className="bg-bg-secondary rounded-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-border-color">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-bold flex items-center gap-2">
                  <CheckCircle className={`w-5 h-5 ${connectionStatus.connected ? 'text-accent-green' : 'text-accent-red'}`} />
                  Alpaca Connection
                </h2>
                <button onClick={() => setShowConnectionModal(false)} className="btn-secondary p-2">✕</button>
              </div>
            </div>
            <div className="p-6">
              {connectionStatus.connected && connectionStatus.details ? (
                <div className="space-y-4">
                  {/* Connection Status */}
                  <div className="flex items-center gap-3 p-4 bg-accent-green/10 border border-accent-green/20 rounded-xl">
                    <div className="w-3 h-3 rounded-full bg-accent-green animate-pulse" />
                    <div>
                      <div className="font-semibold text-accent-green">Connected & Verified</div>
                      <div className="text-xs text-text-muted">Last verified: {new Date(connectionStatus.details.lastVerified).toLocaleString()}</div>
                    </div>
                  </div>
                  
                  {/* Account Details */}
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-border-color">
                      <span className="text-text-muted">Account ID</span>
                      <span className="font-mono text-sm">{connectionStatus.details.accountId?.slice(0, 8)}...</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border-color">
                      <span className="text-text-muted">Account Type</span>
                      <span className={`font-semibold ${connectionStatus.details.accountType === 'Live Trading' ? 'text-accent-red' : 'text-accent-blue'}`}>
                        {connectionStatus.details.accountType}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border-color">
                      <span className="text-text-muted">Status</span>
                      <span className="text-accent-green font-semibold">{connectionStatus.details.status}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border-color">
                      <span className="text-text-muted">API Key</span>
                      <span className="font-mono text-sm">{connectionStatus.details.apiKeyPrefix}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border-color">
                      <span className="text-text-muted">Account Age</span>
                      <span>{connectionStatus.details.accountAgeDays} days</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border-color">
                      <span className="text-text-muted">Equity</span>
                      <span className="font-mono font-semibold">${connectionStatus.details.equity?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border-color">
                      <span className="text-text-muted">Buying Power</span>
                      <span className="font-mono">${connectionStatus.details.buyingPower?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border-color">
                      <span className="text-text-muted">Day Trades Remaining</span>
                      <span className={connectionStatus.details.daytradesRemaining <= 1 ? 'text-accent-red font-semibold' : ''}>
                        {connectionStatus.details.patternDayTrader ? 'PDT (Unlimited)' : `${connectionStatus.details.daytradesRemaining} / 3`}
                      </span>
                    </div>
                  </div>

                  {/* Trading Status */}
                  <div className="grid grid-cols-3 gap-2 mt-4">
                    <div className={`text-center p-2 rounded-lg ${!connectionStatus.details.tradingBlocked ? 'bg-accent-green/10' : 'bg-accent-red/10'}`}>
                      <div className={`text-xs ${!connectionStatus.details.tradingBlocked ? 'text-accent-green' : 'text-accent-red'}`}>
                        Trading
                      </div>
                      <div className={`font-semibold ${!connectionStatus.details.tradingBlocked ? 'text-accent-green' : 'text-accent-red'}`}>
                        {!connectionStatus.details.tradingBlocked ? '✓' : '✕'}
                      </div>
                    </div>
                    <div className={`text-center p-2 rounded-lg ${connectionStatus.details.shortingEnabled ? 'bg-accent-green/10' : 'bg-bg-tertiary'}`}>
                      <div className={`text-xs ${connectionStatus.details.shortingEnabled ? 'text-accent-green' : 'text-text-muted'}`}>
                        Shorting
                      </div>
                      <div className={`font-semibold ${connectionStatus.details.shortingEnabled ? 'text-accent-green' : 'text-text-muted'}`}>
                        {connectionStatus.details.shortingEnabled ? '✓' : '✕'}
                      </div>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-accent-blue/10">
                      <div className="text-xs text-accent-blue">Margin</div>
                      <div className="font-semibold text-accent-blue">{connectionStatus.details.multiplier}x</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <XCircle className="w-12 h-12 text-accent-red mx-auto mb-3" />
                  <p className="text-accent-red font-semibold mb-2">Connection Failed</p>
                  <p className="text-text-muted text-sm">{connectionStatus.error}</p>
                  {connectionStatus.details?.apiKeyPrefix && (
                    <p className="text-text-muted text-xs mt-2">API Key: {connectionStatus.details.apiKeyPrefix}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mb-6"><StrategySelector strategy={strategy} setStrategy={setStrategy} /></div>
      <FilterBar activeFilters={activeFilters} setActiveFilters={setActiveFilters} />

      {accountData ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <StatCard 
            title="Portfolio Value" 
            value={`$${accountData.portfolioValue?.toLocaleString()}`} 
            change={accountData.dayPnlPercent} 
            icon={DollarSign} 
            color="blue" 
            sparkData={sparklineData.portfolio}
          />
          <StatCard 
            title="Available Cash" 
            value={`$${accountData.cash?.toLocaleString()}`} 
            icon={Activity} 
            color="green" 
            sparkData={sparklineData.cash}
          />
          <StatCard 
            title="Buying Power" 
            value={`$${accountData.buyingPower?.toLocaleString()}`} 
            icon={TrendingUp} 
            color="purple" 
            sparkData={sparklineData.buyingPower}
          />
          <StatCard 
            title="Today's P&L" 
            value={`${accountData.dayPnl >= 0 ? '+' : ''}$${accountData.dayPnl?.toLocaleString()}`} 
            change={accountData.dayPnlPercent} 
            icon={BarChart3} 
            color={accountData.dayPnl >= 0 ? 'green' : 'red'} 
            sparkData={sparklineData.pnl}
          />
        </div>
      ) : !hasAlpacaKeys ? (
        <div className="card p-8 text-center mb-8"><Zap className="w-12 h-12 text-accent-blue mx-auto mb-4" /><h3 className="text-xl font-semibold mb-2">Connect Your Alpaca Account</h3><p className="text-text-secondary mb-4">Link your account to see portfolio and execute trades.</p><button onClick={() => setShowAlpacaSetup(true)} className="btn-primary">Connect Alpaca</button></div>
      ) : loading ? <div className="grid grid-cols-4 gap-5 mb-8">{[1,2,3,4].map(i => <div key={i} className="card p-6"><div className="h-8 loading-shimmer rounded" /></div>)}</div> : null}

      {/* Bot Status & WSB Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Bot Status Card */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${botStatus.isRunning ? 'bg-accent-green/15' : 'bg-bg-tertiary'}`}>
                <Bot className={`w-5 h-5 ${botStatus.isRunning ? 'text-accent-green' : 'text-text-muted'}`} />
              </div>
              <div>
                <h3 className="font-semibold">Auto-Trading Bot</h3>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${botStatus.isRunning ? 'bg-accent-green animate-pulse' : 'bg-text-muted'}`} />
                  <span className={`text-sm ${botStatus.isRunning ? 'text-accent-green' : 'text-text-muted'}`}>
                    {botStatus.isRunning ? 'Running' : 'Stopped'}
                  </span>
                  {botConfig?.strategies && (
                    <span className="text-xs text-text-muted">
                      • {botConfig.strategies.map(s => s === 'day' ? '⚡' : s === 'swing' ? '🌊' : '🏔️').join(' ')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <div className={`w-2 h-2 rounded-full ${dayTradingEnabled ? 'bg-accent-green' : 'bg-accent-red'}`} />
                  <span className={`text-xs ${dayTradingEnabled ? 'text-accent-green' : 'text-accent-red'}`}>
                    Day Trading: {dayTradingEnabled === null ? '…' : (dayTradingEnabled ? 'ENABLED' : 'DISABLED')}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <div className={`w-2 h-2 rounded-full ${wsbEnabled ? 'bg-accent-green' : 'bg-accent-red'}`} />
                  <span className={`text-xs ${wsbEnabled ? 'text-accent-green' : 'text-accent-red'}`}>
                    WSB Trading: {wsbEnabled === null ? '…' : (wsbEnabled ? 'ENABLED' : 'DISABLED')}
                  </span>
                </div>
              </div>
            </div>
            <Link to="/admin" className="btn-secondary px-3 py-1.5 text-sm flex items-center gap-1.5">
              <Settings className="w-4 h-4" /> Configure
            </Link>
          </div>
          
          {botStatus.stats ? (
            <div className="grid grid-cols-5 gap-2">
              <div className="bg-bg-tertiary rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-accent-blue">{botStatus.stats.todayTrades || 0}</div>
                <div className="text-xs text-text-muted">Trades</div>
              </div>
              <div className="bg-bg-tertiary rounded-lg p-2 text-center">
                <div className={`text-lg font-bold ${(botStatus.stats.todayPnL || 0) >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                  {(botStatus.stats.todayPnL || 0) >= 0 ? '+' : ''}${(botStatus.stats.todayPnL || 0).toFixed(0)}
                </div>
                <div className="text-xs text-text-muted">P&L</div>
              </div>
              <div className="bg-bg-tertiary rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-accent-purple">
                  {botStatus.stats.wins + botStatus.stats.losses > 0 
                    ? Math.round((botStatus.stats.wins / (botStatus.stats.wins + botStatus.stats.losses)) * 100) 
                    : 0}%
                </div>
                <div className="text-xs text-text-muted">Win Rate</div>
              </div>
              <div className="bg-bg-tertiary rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-text-secondary">${(botStatus.stats.todayVolume || 0).toLocaleString()}</div>
                <div className="text-xs text-text-muted">Volume</div>
              </div>
              <div className="bg-bg-tertiary rounded-lg p-2 text-center" title={`SEC: $${(botStatus.stats.todayFees?.sec || 0).toFixed(4)} | FINRA: $${(botStatus.stats.todayFees?.finraTaf || 0).toFixed(4)}`}>
                <div className="text-lg font-bold text-accent-yellow">${(botStatus.stats.todayFees?.total || 0).toFixed(4)}</div>
                <div className="text-xs text-text-muted">Fees</div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-text-muted text-sm">
              <p>No trading activity yet today</p>
            </div>
          )}
          
          {botConfig?.wsbEnabled && (
            <div className="mt-3 flex items-center gap-2 text-xs text-orange-400 bg-orange-500/10 rounded-lg px-3 py-2">
              <MessageCircle className="w-4 h-4" />
              <span>WSB signals enabled ({botConfig.wsbTradePercent}% position size)</span>
            </div>
          )}
        </div>

        {/* WSB Trending Card */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-500/15 flex items-center justify-center">
                <Flame className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  Trending on WSB
                  <span className="text-xs px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded">LIVE</span>
                </h3>
                <p className="text-xs text-text-muted">r/wallstreetbets sentiment</p>
              </div>
            </div>
            <button onClick={fetchWSBData} disabled={wsbLoading} className="btn-secondary p-2 rounded-lg">
              <RefreshCw className={`w-4 h-4 ${wsbLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          {wsbLoading ? (
            <div className="grid grid-cols-4 gap-2">
              {[1,2,3,4].map(i => (
                <div key={i} className="bg-bg-tertiary rounded-lg p-2 animate-pulse">
                  <div className="h-4 bg-bg-card rounded w-12 mb-1" />
                  <div className="h-3 bg-bg-card rounded w-8" />
                </div>
              ))}
            </div>
          ) : wsbTrending.length > 0 ? (
            <div className="grid grid-cols-4 gap-2">
              {wsbTrending.slice(0, 8).map((ticker, idx) => (
                <div 
                  key={ticker.ticker}
                  className={`rounded-lg p-2 text-center transition-all hover:scale-105 cursor-pointer ${
                    ticker.sentimentLabel === 'bullish' 
                      ? 'bg-accent-green/10 border border-accent-green/20' 
                      : ticker.sentimentLabel === 'bearish'
                      ? 'bg-accent-red/10 border border-accent-red/20'
                      : 'bg-bg-tertiary border border-transparent'
                  }`}
                  onClick={() => {
                    const existing = opportunities.find(o => o.symbol === ticker.ticker);
                    if (existing) setSelectedStock(existing);
                  }}
                >
                  <div className="font-mono font-bold text-sm">${ticker.ticker}</div>
                  <div className="flex items-center justify-center gap-1 text-xs text-text-muted">
                    <Users className="w-3 h-3" />
                    <span>{ticker.mentions}</span>
                    <span className={`font-semibold ${
                      ticker.sentiment >= 60 ? 'text-accent-green' : 
                      ticker.sentiment <= 40 ? 'text-accent-red' : ''
                    }`}>
                      {ticker.sentiment}%
                    </span>
                  </div>
                  {idx < 3 && (
                    <div className={`mt-1 text-xs px-1.5 py-0.5 rounded ${
                      ticker.signal.action?.includes('buy') ? 'bg-accent-green/20 text-accent-green' :
                      ticker.signal.action === 'avoid' ? 'bg-accent-red/20 text-accent-red' :
                      'bg-accent-blue/20 text-accent-blue'
                    }`}>
                      {ticker.signal.action?.replace(/_/g, ' ') || 'watch'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-text-muted text-sm">
              <MessageCircle className="w-6 h-6 mx-auto mb-2 opacity-50" />
              <p>No trending tickers</p>
            </div>
          )}
          
          <div className="mt-3 text-center">
            <a 
              href="https://www.reddit.com/r/wallstreetbets/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-text-muted hover:text-orange-400 flex items-center justify-center gap-1"
            >
              View on Reddit <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      {/* ============ MARKET INTELLIGENCE ROW (Phase 1-5) ============ */}
      <MarketIntelligenceRow />

      <div className="mb-8">
        <div className="flex justify-between items-center mb-5">
          <h2 className="font-display text-xl font-semibold">{strategy === 'day' ? '⚡ Day' : strategy === 'swing' ? '🌊 Swing' : '🏔️ Long Term'} Trading Opportunities {activeFilters.length > 0 && <span className="text-sm font-normal text-text-muted ml-2">({filteredOpportunities.length} results)</span>}</h2>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-bg-card rounded-xl border border-border-color"><Search className="w-5 h-5 text-text-muted" /><input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="bg-transparent border-none outline-none w-48 text-text-primary placeholder:text-text-muted" /></div>
            <Link to="/stocks" className="btn-primary px-4 py-2.5 flex items-center gap-2"><BarChart3 className="w-4 h-4" />View All Stocks</Link>
          </div>
        </div>
        {loading ? <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="card p-5"><div className="h-24 loading-shimmer rounded" /></div>)}</div> : filteredOpportunities.length === 0 ? <div className="card p-12 text-center"><Filter className="w-12 h-12 text-text-muted mx-auto mb-4" /><h3 className="text-lg font-medium mb-2">No matches</h3><p className="text-text-secondary">Try adjusting filters.</p></div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{filteredOpportunities.slice(0, 12).map((stock, i) => <div key={stock.symbol} className="animate-slide-up" style={{ animationDelay: `${i * 50}ms` }}><StockCard stock={stock} onAnalyze={setSelectedStock} onTrade={handleTrade} /></div>)}</div>
        )}
        {filteredOpportunities.length > 12 && (
          <div className="text-center mt-6">
            <Link to="/stocks" className="text-accent-blue hover:underline flex items-center justify-center gap-2">
              View all {filteredOpportunities.length} opportunities <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>

      <PositionsTable positions={positions} onTrade={handleTrade} />

      <footer className="mt-12 card p-6"><p className="text-text-muted text-sm text-center">⚠️ <strong>Disclaimer:</strong> AI analysis for informational purposes only. Not financial advice. All trading involves risk.</p></footer>
      </div>

      {showAlpacaSetup && <AlpacaSetupModal onClose={() => setShowAlpacaSetup(false)} onSave={handleSaveAlpacaKeys} />}
      {selectedStock && <AnalysisPanel stock={selectedStock} strategy={strategy} onClose={() => setSelectedStock(null)} onAiSummary={setShowAiSummary} onTrade={handleTrade} />}
      {showAiSummary && <AiSummaryModal stock={showAiSummary} onClose={() => setShowAiSummary(null)} />}
      {tradeModal && <TradeModal stock={tradeModal.stock} side={tradeModal.side} onClose={() => setTradeModal(null)} onSuccess={fetchData} />}
    </div>
  );
};

export default Dashboard;
